import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import { collection, doc, onSnapshot, runTransaction, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import PageTitle from "../../layouts/PageTitle";
import Toast, { notifyError, notifyTopRight, notifyWarning } from "../toast/SetToast";
import {
  PORTAL_TICKET_ACCESS_COLLECTION, PORTAL_TICKET_TYPES, normalizePortalTicketEmail,
  portalTicketTypesFromAccess, validPortalTicketEmail,
} from "../PatientPortal/patientPortalTicketUtils";

const PatientPortalTicketAccessConfig = () => {
  const { currentUser } = useAuth();
  const isSuperAdmin = Boolean(currentUser && Array.isArray(currentUser.roles) && currentUser.roles.includes("super_admin"));
  const [access, setAccess] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inputs, setInputs] = useState({});
  const [saving, setSaving] = useState(false);
  const [removal, setRemoval] = useState(null);
  const [bookingAccess, setBookingAccess] = useState({ loading: true, enabled: false, error: "" });
  const [savingBookingAccess, setSavingBookingAccess] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    return onSnapshot(doc(db, "patientPortalSettings", "booking"), (snapshot) => {
      const settings = snapshot.data() || {};
      setBookingAccess({ loading: false, enabled: settings.sleepBookingWithoutLinkEnabled === true, error: "" });
    }, () => setBookingAccess({ loading: false, enabled: false, error: "Unable to load sleep booking access. Please try again." }));
  }, [isSuperAdmin]);

  const changeBookingAccess = async () => {
    if (bookingAccess.loading || bookingAccess.error || savingBookingAccess) return;
    setSavingBookingAccess(true);
    try {
      await setDoc(doc(db, "patientPortalSettings", "booking"), {
        sleepBookingWithoutLinkEnabled: !bookingAccess.enabled,
        updatedAt: serverTimestamp(), updatedBy: currentUser.email || currentUser.displayName || "",
      }, { merge: true });
      notifyTopRight("Sleep booking access saved");
    } catch (err) { notifyError("Unable to save sleep booking access. Please try again."); }
    finally { setSavingBookingAccess(false); }
  };

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    return onSnapshot(collection(db, PORTAL_TICKET_ACCESS_COLLECTION), (snapshot) => {
      setAccess(snapshot.docs.map((row) => ({ email: row.id, types: portalTicketTypesFromAccess(row.data()) })).sort((a, b) => a.email.localeCompare(b.email)));
      setLoading(false); setError("");
    }, () => { setError("Unable to load ticket access emails."); setLoading(false); });
  }, [isSuperAdmin]);

  const changeAccess = async (type, rawEmail, add) => {
    const email = normalizePortalTicketEmail(rawEmail);
    if (!validPortalTicketEmail(email)) { notifyWarning("Enter a valid account email."); return; }
    setSaving(true);
    try {
      await runTransaction(db, async (transaction) => {
        const ref = doc(db, PORTAL_TICKET_ACCESS_COLLECTION, email);
        const saved = await transaction.get(ref);
        const current = portalTicketTypesFromAccess(saved.data());
        const types = add ? Array.from(new Set([...current, type])) : current.filter((id) => id !== type);
        if (!types.length) transaction.delete(ref);
        else transaction.set(ref, { email, ticketTypes: types, updatedAt: serverTimestamp(), updatedBy: currentUser.email || currentUser.displayName || "" });
      });
      if (add) setInputs((previous) => ({ ...previous, [type]: "" }));
      setRemoval(null);
      notifyTopRight(add ? "Ticket access added" : "Ticket access removed");
    } catch (err) { notifyError("Unable to save ticket access. Please try again."); }
    finally { setSaving(false); }
  };

  if (!isSuperAdmin) return <Redirect to="/" />;
  return <>
    <Toast />
    <PageTitle activeMenu="Patient Portal Ticket Access" motherMenu="Admin" />
    <div className="card">
      <div className="card-header"><h4 className="card-title mb-0">Sleep booking without a link</h4></div>
      <div className="card-body">
        <p className="text-muted">Disabled by default: patients need a valid customer booking link or QR code to book a sleep test. Enable to allow sleep booking directly from the Patient Portal.</p>
        {bookingAccess.error && <div className="alert alert-danger" role="alert">{bookingAccess.error}</div>}
        <div className="d-flex align-items-center">
          <span className="mr-3" role="status">{bookingAccess.loading ? "Loading…" : bookingAccess.error ? "Unavailable" : <span className={`badge badge-${bookingAccess.enabled ? "success" : "secondary"}`}>{bookingAccess.enabled ? "Enabled — no link required" : "Disabled — booking link required"}</span>}</span>
          <Button size="sm" variant={bookingAccess.enabled ? "outline-danger" : "primary"} disabled={bookingAccess.loading || Boolean(bookingAccess.error) || savingBookingAccess} onClick={changeBookingAccess}>{savingBookingAccess ? "Saving…" : bookingAccess.enabled ? "Disable" : "Enable"}</Button>
        </div>
      </div>
    </div>
    <p className="text-muted">Each email list grants access to that ticket type and its top-right notifications. Add an account to multiple lists when needed. Super Admin accounts also need an email grant to view tickets.</p>
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    <div className="row">
      {PORTAL_TICKET_TYPES.map((type) => {
        const emails = access.filter((row) => row.types.includes(type.id)).map((row) => row.email);
        return <div className="col-xl-4 col-lg-6" key={type.id}>
          <div className="card">
            <div className="card-header"><h4 className="card-title mb-0"><i className={`fa ${type.icon} mr-2`} />{type.label} ({emails.length})</h4></div>
            <div className="card-body">
              <form onSubmit={(event) => { event.preventDefault(); if (!saving && !loading && !error) changeAccess(type.id, inputs[type.id], true); }}>
                <label htmlFor={`portal-access-${type.id}`}>Account email</label>
                <input type="email" id={`portal-access-${type.id}`} className="form-control mb-2" placeholder="name@example.com" value={inputs[type.id] || ""} onChange={(event) => { const value = event.target.value; setInputs((previous) => ({ ...previous, [type.id]: value })); }} disabled={saving || loading || Boolean(error)} required />
                <Button type="submit" size="sm" disabled={saving || loading || Boolean(error)}>Add access</Button>
              </form>
              <div className="mt-3">
                {loading ? <span className="text-muted">Loading emails…</span> : emails.length ? emails.map((email) => <div className="d-flex align-items-center border-bottom py-2" key={email}>
                  <span className="small mr-2" style={{ overflowWrap: "anywhere" }}>{email}</span>
                  <Button size="sm" variant="outline-danger" className="ml-auto" disabled={saving} onClick={() => setRemoval({ type, email })} aria-label={`Remove ${email} from ${type.label}`}>Remove</Button>
                </div>) : <span className="text-muted">No accounts configured.</span>}
              </div>
            </div>
          </div>
        </div>;
      })}
    </div>
    <Modal show={Boolean(removal)} onHide={() => { if (!saving) setRemoval(null); }} centered>
      <Modal.Header closeButton={!saving}><Modal.Title>Remove ticket access?</Modal.Title></Modal.Header>
      <Modal.Body>{removal && <>Remove <strong>{removal.email}</strong> from {removal.type.label}? Access to other ticket types remains as configured.</>}</Modal.Body>
      <Modal.Footer><Button variant="light" disabled={saving} onClick={() => setRemoval(null)}>Cancel</Button><Button variant="danger" disabled={saving} onClick={() => changeAccess(removal.type.id, removal.email, false)}>{saving ? "Removing…" : "Remove access"}</Button></Modal.Footer>
    </Modal>
  </>;
};
export default PatientPortalTicketAccessConfig;
